import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Stack, Button } from '@mui/material';
import { CreateDebateModal } from 'components/Modals/CreateDebateModal/CreateDebateModal';
import { MobileDebateCreationModal } from 'components/Modals/MobileDebateCreationModal/MobileDebateCreationModal';
import HeaderContextMenu from 'components/Menu/HeaderContextMenu/HeaderContextMenu';
import { AuthModal } from 'components/Modals/AuthModal/AuthModal';
import { selectIsEmailVerified, selectSpecialPage, selectIsAuth } from 'redux/selectors/selectors';
import { setSpecialPage } from 'redux/slices/app/app.slice';
import { RestorePasswordModal } from 'components/Modals/RestorePasswordModal/RestorePasswordModal';
import { openCurrentModal, openAlertDialog } from 'redux/slices/modals/modals.slice';
import { useGetProfileQuery } from 'redux/api/user.api';
import { useEndDebateMutation } from 'redux/api/debate.api';
import { useLoading } from 'shared/hooks/useLoading';
import { useAppDispatch, useAppSelector } from 'redux/hooks/reduxHooks';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { TAlertDialog, EAvatarTypes } from 'shared/types/card.interface';
import { AlertDialog } from 'components/Modals/AlertDialog/AlertDialog';
import { DEBATE_ENDING_OR_LEAVING } from 'shared/constants/alert-constants';
import { AUTH_MODAL_DATA } from 'shared/constants/modal-constants';
import { useCommonDialog } from 'shared/hooks/useCommonDialog';
import { HEADER_BUTTONS } from 'shared/constants/buttons-constants';
import { EPageTypes } from 'shared/types/common.interface';
import { ConfirmEmailModal } from 'components/Modals/ConfirmEmailModal/ConfirmEmailModal';
import { LoginModal } from 'components/Modals/LoginModal/LoginModal';
import { PopupNotification } from 'components/Modals/PopupNotification/PopupNotification';
import { EModalsNames } from 'shared/types/modal.interface';
import { urls } from 'shared/constants/routing.constants';
import { LOCAL_STORAGE } from 'shared/constants/local-storage-constants';
import { CustomMuiIcon } from 'assets/images/icons/customMuiIcons';
import { Avatar } from 'components/Avatar/Avatar';
import { Logo } from 'components/Logo/Logo';

const Header: React.FC = () => {
  const [contextMenuIsOpen, setContextMenuIsOpen] = useState(false);
  const isEmailVerified = useAppSelector(selectIsEmailVerified);
  const dispatch = useAppDispatch();
  const currentPage = useAppSelector(selectSpecialPage);
  const location = useLocation();
  const debateId = localStorage.getItem(LOCAL_STORAGE.currentDebateId);

  const isAuth = useAppSelector(selectIsAuth);
  const { data: profileData = null } = useGetProfileQuery(undefined, { skip: !isAuth });
  const [endDebate, { isLoading: isLoadingEndDebate }] = useEndDebateMutation();
  useLoading(isLoadingEndDebate);

  const logoDialog = useCommonDialog(urls.commonPages.home);
  const aboutDialog = useCommonDialog(urls.commonPages.about);
  const createWithDialog = useCommonDialog(urls.commonPages.generateTopics);

  useEffect(() => {
    if (isEmailVerified) {
      dispatch(openCurrentModal({ popupName: EModalsNames.AuthModal }));
    }
  }, [isEmailVerified, dispatch]);

  const openModal = () => {
    if (profileData) {
      dispatch(openCurrentModal({ popupName: EModalsNames.CreateDebate }));
    } else {
      dispatch(
        openCurrentModal({
          popupName: EModalsNames.AuthModal,
          data: { subtitle: AUTH_MODAL_DATA.subtitleOnChallenge },
        }),
      );
    }
  };

  const openContextMenu = () => {
    setContextMenuIsOpen(!contextMenuIsOpen);
  };

  const toLink = useCallback(
    async (dialogInfo: TAlertDialog, isLogout?: boolean) => {
      if (currentPage === EPageTypes.debate) {
        await new Promise<boolean>((resolve) => {
          if (resolve) {
            dispatch(
              openAlertDialog({
                title: dialogInfo.title,
                text: dialogInfo.text,
                сonsentText: dialogInfo.сonsentText,
                сonsentCallback: () => {
                  endDebate({
                    id: debateId,
                    message: DEBATE_ENDING_OR_LEAVING.youLeft,
                  });
                  resolve(true);
                },
                dissentText: dialogInfo.dissentText,
              }),
            );
          }
        });
        dialogInfo.сonsentCallback?.();
      } else if (isLogout) {
        dispatch(
          openAlertDialog({
            title: dialogInfo.title,
            text: dialogInfo.text,
            сonsentText: dialogInfo.сonsentText,
            сonsentCallback: () => dialogInfo.сonsentCallback(),
            dissentText: dialogInfo.dissentText,
          }),
        );
      } else {
        dialogInfo.сonsentCallback?.();
      }

      setContextMenuIsOpen(false);
    },
    [currentPage, debateId, dispatch, endDebate],
  );

  const toPageCreateWithAI = () => {
    if (profileData) {
      toLink(createWithDialog);
    } else {
      localStorage.setItem(LOCAL_STORAGE.toGenerateWithAI, LOCAL_STORAGE.toGenerateWithAI);
      dispatch(
        openCurrentModal({
          popupName: EModalsNames.AuthModal,
          data: { subtitle: AUTH_MODAL_DATA.subtitleOnChallenge },
        }),
      );
    }
  };

  useEffect(() => {
    if (location.pathname.startsWith(urls.myPages.video)) {
      dispatch(setSpecialPage(EPageTypes.debate));
    } else if (location.pathname.startsWith(urls.commonPages.stream)) {
      dispatch(setSpecialPage(EPageTypes.stream));
    } else {
      dispatch(setSpecialPage(null));
    }
  }, [location, dispatch]);

  return (
    <Stack
      sx={(theme) => ({
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        padding: { lg: '24px 40px', md: '15px 22px', sm: '15px 22px', xs: '15px 22px' },
        boxShadow: `0px 4px 10px 0px ${theme.palette.myGrey.grey70}`,
        position: { md: 'sticky', xs: 'static' },
        top: 0,
        zIndex: 10,
        backgroundColor: 'primary.contrastText',
      })}
    >
      <Logo />
      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{
          display: { md: 'flex', sm: 'none', xs: 'none' },
          columnGap: { lg: '16px', md: '8px' },
        }}
      >
        {location.pathname === urls.commonPages.about && (
          <Button variant="transparent" onClick={() => toLink(logoDialog)}>
            {HEADER_BUTTONS.toDebates}
          </Button>
        )}
        {location.pathname !== urls.commonPages.about && (
          <Button variant="transparent" onClick={() => toLink(aboutDialog)}>
            {HEADER_BUTTONS.about}
          </Button>
        )}
        <Button
          variant="defaultLight"
          onClick={toPageCreateWithAI}
          sx={{ backgroundColor: 'myGrey.grey100' }}
        >
          <CustomMuiIcon type="icon-create-with-AI" />
          {HEADER_BUTTONS.createWithAI}
        </Button>
        <Button variant="autoSize" onClick={openModal}>
          {HEADER_BUTTONS.startYourDebate}
        </Button>
        {profileData ? (
          <ClickAwayListener onClickAway={() => setContextMenuIsOpen(false)}>
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                onClick={openContextMenu}
              >
                <Avatar
                  type={EAvatarTypes.Header}
                  firstName={profileData.first_name}
                  lastName={profileData.last_name}
                />
              </Box>
              <HeaderContextMenu contextMenuIsOpen={contextMenuIsOpen} handlerClick={toLink} />
            </Box>
          </ClickAwayListener>
        ) : (
          <Button
            variant="defaultLight"
            onClick={() => dispatch(openCurrentModal({ popupName: EModalsNames.AuthModal }))}
          >
            {HEADER_BUTTONS.signIn}
          </Button>
        )}
      </Stack>
      <CreateDebateModal />
      <AuthModal />
      <RestorePasswordModal />
      <AlertDialog />
      <ConfirmEmailModal />
      <LoginModal />
      <PopupNotification />
      <MobileDebateCreationModal />
    </Stack>
  );
};

export default Header;
